import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { SystemUserInjection } from "features/systemUserManagement/di/SystemUserInjection";
import { SystemUserFormRequestEntity } from "features/systemUserManagement/domain/entities/SystemUserFormRequestEntity";
import { SystemUserRequestEntity } from "features/systemUserManagement/domain/entities/SystemUserRequestEntity";
import { SystemUserResponseEntity } from "features/systemUserManagement/domain/entities/SystemUserResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { ISystemUserObservedState } from "../interfaces/ISystemUser";

// ─── Input ────────────────────────────────────────────────────────────────────

class SystemUserInput implements Disposable {
  public fetchList = new BehaviorSubject<SystemUserRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public fetchRoles = new BehaviorSubject<void>(undefined);
  public fetchPartner = new BehaviorSubject<BaseFilterListRequestModel>({
    page: 0,
    limit: 100,
  });
  public fetchProviders = new BehaviorSubject<BaseFilterListRequestModel>({
    page: 0,
    limit: 100,
  });
  public fetchSystemUserFeatures =
    new BehaviorSubject<BaseFilterListRequestModel>({
      page: 0,
      limit: 500,
    });
  public fetchSystemUserMenus =
    new BehaviorSubject<BaseFilterListRequestModel>({
      page: 0,
      limit: 500,
    });
  public createUser = new BehaviorSubject<{
    body?: SystemUserFormRequestEntity;
    detailInfo?: SystemUserResponseEntity | null;
  }>({});
  public updateUser = new BehaviorSubject<{
    body?: SystemUserFormRequestEntity;
    detailInfo?: SystemUserResponseEntity | null;
  }>({});
  public deleteUser = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchPartner.complete();
    this.fetchProviders.complete();
    this.fetchSystemUserFeatures.complete();
    this.fetchSystemUserMenus.complete();
    this.fetchRoles.complete();
    this.createUser.complete();
    this.updateUser.complete();
    this.deleteUser.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class SystemUserOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<
    "create" | "update" | "delete" | null
  >(null);
  public onChangePassSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.actionSuccess.complete();
    this.onChangePassSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class SystemUserViewModel extends BasedObservedViewModel<
  SystemUserInput,
  SystemUserOutput,
  ISystemUserObservedState
> {
  constructor() {
    super(new SystemUserInput(), new SystemUserOutput());
  }

  private useCase = SystemUserInjection.getUseCase();

  protected override createObservedState(): ISystemUserObservedState {
    return {
      users: [],
      loading: false,
      formLoading: false,
      detailLoading: false,
      detail: null,
      paging: { page: 0, limit: 10, total: 0 },
      roleOption: [],
      partnerList: [],
      providerList: [],
      featureList: [],
      menuList: [],
    };
  }

  protected override transform(): void {
    // Fetch user list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.getUsers(params);
        if (res?.data) {
          this.setObservedState({
            users: res.data,
            paging: res.pagination,
          });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch user detail
    this.skipFirstInitial(this.input.fetchDetail, async (code) => {
      if (!code) return;
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.useCase.getSystemUserDetail(code);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    // Fetch roles for filter/form dropdowns (silent background fetch — no loading indicator)
    this.skipFirstInitial(this.input.fetchRoles, async () => {
      try {
        const res = await this.useCase.getRoleList({
          limit: 1000,
          order: "name asc",
        });
        if (res) {
          this.setObservedState({
            roleOption: [
              {
                label: strings.GlobalLabels.All,
                value: strings.GlobalLabels.All,
              },
              ...res.map((role) => ({
                label: role?.name,
                value: role?.code,
              })),
            ],
          });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });

    // Create user
    this.skipFirstInitial(this.input.createUser, async (props) => {
      const { body, detailInfo } = props;
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.createUser(
          body,
          detailInfo ?? undefined
        );
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Update user
    this.skipFirstInitial(this.input.updateUser, async (props) => {
      const { body, detailInfo } = props;
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.updateUser(
          body,
          detailInfo ?? undefined
        );
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Delete user
    this.skipFirstInitial(this.input.deleteUser, async (code) => {
      if (!code) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.deleteUser(code);
        if (res?.data) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      try {
        const res = await this.useCase.getPartnerList(params);
        if (res) {
          this.setObservedState({ partnerList: res });
        }
      } catch (error) {
        console.log(error);
      }
    });

    this.skipFirstInitial(this.input.fetchProviders, async (params) => {
      try {
        const res = await this.useCase.getProviderList(params);
        if (res) {
          this.setObservedState({ providerList: res });
        }
      } catch (error) {
        console.log(error);
      }
    });

    this.skipFirstInitial(
      this.input.fetchSystemUserFeatures,
      async (params) => {
        try {
          const res = await this.useCase.getSystemUserFeatureList(params);
          if (res) {
            this.setObservedState({ featureList: res });
          }
        } catch (error) {
          console.error(error);
          this.output.onError.next(
            error?.message || strings.GlobalMessage.Error,
          );
        }
      },
    );

    this.skipFirstInitial(this.input.fetchSystemUserMenus, async (params) => {
      try {
        const res: BasePermissionMenu[] =
          await this.useCase.getSystemUserMenuList(params);
        if (res) {
          this.setObservedState({ menuList: res });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });
  }
}
